// School Onboarding Wizard — Multi-step wizard for Platform Admin
import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePricingConfig } from '../hooks/usePricingConfig';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = {
  post: async (endpoint, data, token) => {
    const res = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return res.json();
  },
  put: async (endpoint, data, token) => {
    const res = await fetch(`${API_URL}${endpoint}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return res.json();
  }
};

export default function OnboardingWizard({ isOpen, onClose, token, onSuccess }) {
  const { language } = useLanguage();
  const { config: pricingCfg } = usePricingConfig();
  const BASE_PRICE = pricingCfg.base_price_per_user_year;
  const FOUNDING_PRICE = pricingCfg.founding_price_per_user_year;
  const FOUNDING_PCT = pricingCfg.founding_discount_percent;
  const FOUNDING_LIMIT = pricingCfg.founding_schools_limit;
  const CURR = pricingCfg.currency_symbol;
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const [school, setSchool] = useState({ name: '', subdomain: '', board: 'CBSE', medium: 'Hindi Medium', phone: '', email: '', principal_name: '', city: '', district: '', state: 'Madhya Pradesh', pincode: '', address_line1: '', code: '', established_year: '' });
  const [admin, setAdmin] = useState({ name: '', phone: '', password: '', email: '' });
  // Pricing model: ₹BASE_PRICE/user/year (single plan, 100% FREE ERP + AI).
  // estimated_users = admin-entered rough user count for year-1 invoice preview.
  const [plan, setPlan] = useState({ estimated_users: 100 });
  const [perms, setPerms] = useState({ can_onboard_students: true, can_manage_fees: true, can_manage_transport: true, can_manage_hr: true, can_send_whatsapp: true, can_use_ai: true });
  const resolveTier = (n) => (pricingCfg.pricing_tiers || []).find((x) => n >= x.min && (x.max == null || n <= x.max)) || null;
  const tierTotal = (n) => { const tr = resolveTier(n); return tr && !tr.custom ? tr.one_time + tr.annual_maintenance : 0; };

  // UDISE auto-fill state
  const [udiseLookup, setUdiseLookup] = useState({ loading: false, status: null, message: '' });

  const lookupUdise = async () => {
    const code = (school.code || '').trim();
    if (!/^\d{11}$/.test(code)) {
      setUdiseLookup({ loading: false, status: 'error', message: language === 'hi' ? '11-digit UDISE code ज़रूरी है' : '11-digit UDISE code required' });
      return;
    }
    setUdiseLookup({ loading: true, status: null, message: '' });
    try {
      const r = await fetch(`${API_URL}/api/udise/lookup-public/${code}`);
      const data = await r.json();
      if (data.success && data.found) {
        const p = data.preview || {};
        setSchool(prev => ({
          ...prev,
          name: prev.name || p.school_name || '',
          district: prev.district || p.district || '',
          state: prev.state || p.state || prev.state,
          board: p.board && p.board !== 'Unknown' ? p.board : prev.board,
          established_year: prev.established_year || (p.established_year ? String(p.established_year) : ''),
        }));
        setUdiseLookup({
          loading: false,
          status: 'success',
          message: language === 'hi'
            ? `✅ ${p.school_name} मिली — fields auto-fill हो गए!`
            : `✅ ${p.school_name} found — fields auto-filled!`,
        });
      } else {
        setUdiseLookup({
          loading: false,
          status: 'not_found',
          message: language === 'hi'
            ? '🤔 इस UDISE code की जानकारी नहीं मिली। आप manually भर सकते हैं।'
            : '🤔 UDISE code not found. Fill details manually.',
        });
      }
    } catch (e) {
      setUdiseLookup({ loading: false, status: 'error', message: 'Network error' });
    }
  };

  const handleCreate = async () => {
    setLoading(true);
    try {
      const schoolRes = await api.post('/api/schools/', {
        name: school.name, subdomain: school.subdomain, board: school.board, medium: school.medium,
        phone: school.phone, email: school.email, principal_name: school.principal_name,
        code: school.code || null, established_year: school.established_year ? Number(school.established_year) : null,
        address: { line1: school.address_line1 || school.city, line2: '', city: school.city, district: school.district, state: school.state, pincode: school.pincode }
      }, token);

      const schoolId = schoolRes.id || schoolRes.school_id;
      if (!schoolId) { setLoading(false); return; }

      try {
        await api.post('/api/auth/register', {
          name: admin.name || school.principal_name, phone: admin.phone || school.phone,
          password: admin.password || 'School@123', email: admin.email || school.email,
          role: 'school_admin', school_id: schoolId,
        }, token);
      } catch (e) { console.error('Admin creation:', e); }

      try { await api.put('/api/school-permissions/update', { school_id: schoolId, ...perms }, token); } catch (e) {}

      // Save onboarding record
      try {
        await api.post('/api/onboarding/record', {
          school_id: schoolId, school_name: school.name,
          admin_phone: admin.phone || school.phone, admin_name: admin.name || school.principal_name,
          plan: 'shiksha_mitra_free', estimated_users: plan.estimated_users, setup_cost: tierTotal(plan.estimated_users),
        }, token);
      } catch (e) { console.error('Onboarding record:', e); }

      // WhatsApp welcome message to admin
      try {
        await fetch(`${API_URL}/api/whatsapp/send-template`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            phone: admin.phone || school.phone,
            template_name: 'platform_intro',
            language: 'en_US',
            parameters: [admin.name || school.principal_name || school.name],
          })
        });
      } catch (e) { console.error('WhatsApp welcome:', e); }

      setResult({ schoolId, schoolName: school.name, adminPhone: admin.phone || school.phone, plan: 'shiksha_mitra_free' });
      setStep(6);
    } catch (err) { console.error('Onboarding error:', err); }
    setLoading(false);
  };

  const handleDone = () => {
    setStep(1); setResult(null);
    setSchool({ name: '', subdomain: '', board: 'CBSE', medium: 'Hindi Medium', phone: '', email: '', principal_name: '', city: '', district: '', state: 'Madhya Pradesh', pincode: '' });
    setAdmin({ name: '', phone: '', password: '', email: '' });
    if (onSuccess) onSuccess();
    onClose();
  };

  if (!isOpen) return null;

  // Single FREE plan — revenue is from ₹BASE_PRICE/user/year yearly subscription (not tiered).
  // Founding offer: first ${FOUNDING_LIMIT} schools get ${FOUNDING_PCT}% OFF first year.
  // Source of truth: /app/backend/config/pricing.py

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="onboarding-wizard">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b bg-gradient-to-r from-orange-500 to-amber-500 rounded-t-2xl">
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-white font-bold text-lg" style={{fontFamily:'Outfit'}}>
              {step <= 5 ? (language === 'hi' ? 'School Onboarding Wizard' : 'School Onboarding Wizard') : ''}
            </h2>
            <button onClick={onClose} className="text-white/70 hover:text-white text-xl">&times;</button>
          </div>
          {step <= 5 && (<div className="flex gap-1">{[1,2,3,4,5].map(s => (<div key={s} className={`h-1.5 flex-1 rounded-full ${s <= step ? 'bg-white' : 'bg-white/30'}`} />))}</div>)}
        </div>

        <div className="p-5">
          {step === 1 && (
            <div className="space-y-4" data-testid="wizard-step-1">
              <div className="text-center mb-4"><div className="text-4xl mb-2">🏫</div><h3 className="font-bold text-gray-800">Step 1: School Details</h3><p className="text-xs text-gray-400">{language === 'hi' ? 'School की basic जानकारी' : 'Basic info'}</p></div>

              {/* UDISE Auto-fill — top of step 1 */}
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200 rounded-xl p-4" data-testid="udise-autofill-block">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-bold text-orange-900 uppercase tracking-wider">
                    ⚡ {language === 'hi' ? 'Quick Fill — UDISE Code' : 'Quick Fill — UDISE Code'}
                  </label>
                  <a href="https://udiseplus.gov.in" target="_blank" rel="noopener noreferrer" className="text-[11px] text-orange-700 underline hover:text-orange-800">
                    udiseplus.gov.in
                  </a>
                </div>
                <div className="flex gap-2">
                  <input
                    placeholder={language === 'hi' ? '11-digit UDISE code' : '11-digit UDISE code'}
                    value={school.code}
                    maxLength={11}
                    onChange={e => { setSchool({...school, code: e.target.value.replace(/\D/g, '')}); setUdiseLookup({loading: false, status: null, message: ''}); }}
                    className="flex-1 px-3 py-2 border border-orange-300 rounded-lg font-mono tracking-wider focus:border-orange-500 outline-none bg-white"
                    data-testid="wiz-udise"
                  />
                  <button
                    onClick={lookupUdise}
                    disabled={udiseLookup.loading || school.code.length !== 11}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap text-sm"
                    data-testid="wiz-udise-lookup-btn"
                  >
                    {udiseLookup.loading ? '⏳' : '🔍'} {language === 'hi' ? 'Auto-fill' : 'Auto-fill'}
                  </button>
                </div>
                {udiseLookup.message && (
                  <p className={`text-xs mt-2 ${
                    udiseLookup.status === 'success' ? 'text-green-700' :
                    udiseLookup.status === 'not_found' ? 'text-amber-700' : 'text-red-600'
                  }`} data-testid="wiz-udise-message">
                    {udiseLookup.message}
                  </p>
                )}
                <p className="text-[11px] text-orange-700 mt-1.5">
                  {language === 'hi' ? '💡 UDISE code डालते ही school name, board, district, state auto-fill हो जाएँगे।' : '💡 Enter UDISE code — name, board, district, state auto-fill.'}
                </p>
              </div>

              <input placeholder={language === 'hi' ? 'School का नाम *' : 'School Name *'} value={school.name} onChange={e => setSchool({...school, name: e.target.value})} className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-orange-400 outline-none" data-testid="wiz-school-name" />
              <input placeholder="Subdomain *" value={school.subdomain} onChange={e => setSchool({...school, subdomain: e.target.value})} className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-orange-400 outline-none" data-testid="wiz-subdomain" />
              <div className="grid grid-cols-2 gap-3">
                <select value={school.board} onChange={e => setSchool({...school, board: e.target.value})} className="px-4 py-2.5 border-2 border-gray-200 rounded-xl" data-testid="wiz-board">
                  {['CBSE','ICSE','MP Board','State Board','UP Board','Bihar Board','Rajasthan Board'].map(b => <option key={b}>{b}</option>)}
                </select>
                <select value={school.medium} onChange={e => setSchool({...school, medium: e.target.value})} className="px-4 py-2.5 border-2 border-gray-200 rounded-xl">
                  {['Hindi Medium','English Medium','Both'].map(m => <option key={m}>{m}</option>)}
                </select>
              </div>
              <input placeholder="Phone *" value={school.phone} onChange={e => setSchool({...school, phone: e.target.value})} className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-orange-400 outline-none" data-testid="wiz-phone" />
              <input placeholder="Email" type="email" value={school.email} onChange={e => setSchool({...school, email: e.target.value})} className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-orange-400 outline-none" />
              <input placeholder={language === 'hi' ? 'Principal का नाम' : 'Principal Name'} value={school.principal_name} onChange={e => setSchool({...school, principal_name: e.target.value})} className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-orange-400 outline-none" />
              <input placeholder={language === 'hi' ? 'पूरा पता (Address Line 1)' : 'Full Address (Line 1)'} value={school.address_line1} onChange={e => setSchool({...school, address_line1: e.target.value})} className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-orange-400 outline-none" data-testid="wiz-address" />
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="City *" value={school.city} onChange={e => setSchool({...school, city: e.target.value})} className="px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-orange-400 outline-none" />
                <input placeholder="District" value={school.district} onChange={e => setSchool({...school, district: e.target.value})} className="px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-orange-400 outline-none" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <input placeholder="State" value={school.state} onChange={e => setSchool({...school, state: e.target.value})} className="px-4 py-2.5 border-2 border-gray-200 rounded-xl" />
                <input placeholder="Pincode" value={school.pincode} onChange={e => setSchool({...school, pincode: e.target.value})} className="px-4 py-2.5 border-2 border-gray-200 rounded-xl" />
                <input placeholder={language === 'hi' ? 'स्थापना वर्ष' : 'Est. Year'} type="number" value={school.established_year} onChange={e => setSchool({...school, established_year: e.target.value})} className="px-4 py-2.5 border-2 border-gray-200 rounded-xl" data-testid="wiz-est-year" />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="wizard-step-2">
              <div className="text-center mb-4"><div className="text-4xl mb-2">👤</div><h3 className="font-bold text-gray-800">Step 2: School Admin Account</h3><p className="text-xs text-gray-400">{language === 'hi' ? 'Login credentials बनाएं' : 'Create login credentials'}</p></div>
              <input placeholder={language === 'hi' ? 'Admin का नाम *' : 'Admin Name *'} value={admin.name} onChange={e => setAdmin({...admin, name: e.target.value})} className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-orange-400 outline-none" data-testid="wiz-admin-name" />
              <input placeholder="Admin Phone * (Login ID)" value={admin.phone} onChange={e => setAdmin({...admin, phone: e.target.value})} className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-orange-400 outline-none" data-testid="wiz-admin-phone" />
              <input placeholder="Password *" type="password" value={admin.password} onChange={e => setAdmin({...admin, password: e.target.value})} className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-orange-400 outline-none" data-testid="wiz-admin-password" />
              <input placeholder="Email (optional)" type="email" value={admin.email} onChange={e => setAdmin({...admin, email: e.target.value})} className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-orange-400 outline-none" />
              <div className="bg-blue-50 rounded-xl p-3 text-sm text-blue-700"><i className="fa-solid fa-info-circle mr-1"></i>{language === 'hi' ? 'School Admin इस Phone + Password से login करेगा' : 'Admin will login with this Phone + Password'}</div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="wizard-step-3">
              <div className="text-center mb-4"><div className="text-4xl mb-2">🎁</div><h3 className="font-bold text-gray-800">{language === 'hi' ? 'Step 3: Plan' : 'Step 3: Plan'}</h3></div>
              <div className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl p-5 text-white text-center shadow-lg" data-testid="plan-free-card">
                <span className="text-[10px] bg-white/25 text-white px-2 py-0.5 rounded-full">{language === 'hi' ? 'एक ही Plan' : 'Single Plan'}</span>
                <h4 className="font-black text-2xl mt-2" style={{fontFamily:'Outfit'}}>शिक्षा मित्र FREE</h4>
                <p className="text-4xl font-black mt-2">₹0</p>
                <p className="text-xs text-white/80 mt-1">{language === 'hi' ? 'पूरा Platform FREE' : 'Entire Platform FREE'}</p>
                <div className="border-t border-white/30 mt-3 pt-3 text-left text-xs space-y-1">
                  <div>✓ {language === 'hi' ? 'सभी 8 ERP Modules' : 'All 8 ERP Modules'}</div>
                  <div>✓ {language === 'hi' ? '31 AI Features (school का Gemini Key)' : '31 AI Features (school\'s Gemini key)'}</div>
                  <div>✓ {language === 'hi' ? 'Admission Engine + Campaign Manager' : 'Admission Engine + Campaign Manager'}</div>
                  <div>✓ {language === 'hi' ? 'Parent + Student App' : 'Parent + Student App'}</div>
                </div>
              </div>
              <div className="bg-blue-50 rounded-xl p-3 text-xs text-blue-700">
                <i className="fa-solid fa-info-circle mr-1"></i>
                {language === 'hi'
                  ? `Revenue सिर्फ one-time + maintenance से (छात्रों की संख्या पर आधारित slab): ${CURR}${pricingCfg.one_time_price}+ one-time + ${CURR}${pricingCfg.annual_maintenance}+/साल। Platform 100% FREE।`
                  : `Revenue only from one-time + maintenance (student-count based slab): ${CURR}${pricingCfg.one_time_price}+ one-time + ${CURR}${pricingCfg.annual_maintenance}+/year. Platform 100% FREE.`}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4" data-testid="wizard-step-4">
              <div className="text-center mb-4"><div className="text-4xl mb-2">💰</div><h3 className="font-bold text-gray-800">{language === 'hi' ? 'Step 4: छात्रों की संख्या से Plan चुनें' : 'Step 4: Pick Plan by Student Count'}</h3></div>
              <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl p-5 text-white text-center">
                <p className="text-sm text-slate-400 mb-1">{language === 'hi' ? 'आपके स्कूल में कुल छात्र' : 'Total students in your school'}</p>
                <input type="number" min="1" value={plan.estimated_users}
                  onChange={e => setPlan({...plan, estimated_users: Math.max(1, Number(e.target.value) || 0)})}
                  className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-3xl font-black text-center w-40 outline-none" data-testid="wiz-estimated-users" />
                {(() => { const tr = resolveTier(plan.estimated_users); return tr ? (<p className="text-slate-400 text-xs mt-2" data-testid="wiz-tier-label">{language === 'hi' ? tr.label_hi : tr.label_en}</p>) : null; })()}
              </div>
              {(() => {
                const tr = resolveTier(plan.estimated_users);
                if (tr && tr.custom) {
                  return (
                    <div className="bg-slate-100 rounded-xl p-4 border border-slate-200 text-center">
                      <p className="text-sm font-semibold text-slate-800 mb-1">{language === 'hi' ? '🏛️ 5000+ छात्र — Custom Pricing' : '🏛️ 5000+ students — Custom Pricing'}</p>
                      <p className="text-xs text-slate-500">{language === 'hi' ? 'Sales team (9826618603) से संपर्क करें।' : 'Contact sales team (9826618603).'}</p>
                    </div>
                  );
                }
                const oneTime = tr ? tr.one_time : 0;
                const maint = tr ? tr.annual_maintenance : 0;
                return (
                  <div className="bg-orange-50 rounded-xl p-4 border border-orange-100">
                    <p className="text-sm text-orange-800 font-medium mb-2">{language === 'hi' ? 'पहला भुगतान (estimated):' : 'First Payment (estimated):'}</p>
                    <div className="flex justify-between text-sm"><span className="text-gray-600">{language === 'hi' ? 'One-time (license + setup)' : 'One-time (license + setup)'}</span><span className="font-medium">{CURR}{oneTime.toLocaleString('en-IN')}</span></div>
                    <div className="flex justify-between text-sm mt-1"><span className="text-gray-600">{language === 'hi' ? 'सालाना maintenance' : 'Annual maintenance'}</span><span className="font-medium">{CURR}{maint.toLocaleString('en-IN')}{language === 'hi' ? '/साल' : '/yr'}</span></div>
                    <hr className="my-2 border-orange-200" />
                    <div className="flex justify-between font-bold text-orange-700"><span>{language === 'hi' ? 'अभी देय' : 'Payable now'}</span><span data-testid="wiz-total-cost">{CURR}{(oneTime + maint).toLocaleString('en-IN')}</span></div>
                    <div className="text-[11px] text-gray-500 mt-2">{language === 'hi' ? `Software lifetime free — अगले साल से सिर्फ ${CURR}${maint.toLocaleString('en-IN')}/साल maintenance।` : `Software lifetime free — only ${CURR}${maint.toLocaleString('en-IN')}/year maintenance from next year.`}</div>
                  </div>
                );
              })()}
            </div>
          )}

          {step === 5 && (
            <div className="space-y-4" data-testid="wizard-step-5">
              <div className="text-center mb-4"><div className="text-4xl mb-2">🔐</div><h3 className="font-bold text-gray-800">{language === 'hi' ? 'Step 5: Permissions और Review' : 'Step 5: Permissions & Review'}</h3></div>
              <div className="space-y-2">
                {[
                  { key: 'can_onboard_students', label: 'Student Onboarding' },
                  { key: 'can_manage_fees', label: 'Fee Management' },
                  { key: 'can_manage_transport', label: 'Transport' },
                  { key: 'can_manage_hr', label: 'HR & Payroll' },
                  { key: 'can_send_whatsapp', label: 'WhatsApp' },
                  { key: 'can_use_ai', label: 'AI Features' },
                ].map(p => (
                  <div key={p.key} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-700">{p.label}</span>
                    <button onClick={() => setPerms({...perms, [p.key]: !perms[p.key]})} className={`w-10 h-5 rounded-full transition ${perms[p.key] ? 'bg-green-500' : 'bg-gray-300'}`}>
                      <span className={`block w-4 h-4 bg-white rounded-full shadow transition-transform ${perms[p.key] ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="bg-slate-50 rounded-xl p-4 border text-sm space-y-1">
                <p><strong>School:</strong> {school.name} ({school.board})</p>
                <p><strong>Admin:</strong> {admin.name || school.principal_name} — {admin.phone || school.phone}</p>
                <p><strong>Plan:</strong> शिक्षा मित्र software lifetime FREE + one-time {CURR}{pricingCfg.one_time_price} (license+setup) + {CURR}{pricingCfg.annual_maintenance}/year maintenance · AI आपकी अपनी Gemini key पर (BYOK)</p>
                <p><strong>{language === 'hi' ? 'शहर:' : 'City:'}</strong> {school.city}, {school.state}</p>
              </div>
            </div>
          )}

          {step === 6 && result && (
            <div className="text-center py-6" data-testid="wizard-success">
              <div className="text-6xl mb-4">🎉</div>
              <h2 className="text-xl font-bold text-green-700" style={{fontFamily:'Outfit'}}>{language === 'hi' ? 'School Onboard हो गया!' : 'School Onboarded!'}</h2>
              <div className="bg-green-50 rounded-xl p-4 border border-green-200 mt-4 text-left text-sm space-y-1">
                <p><strong>School:</strong> {result.schoolName}</p>
                <p><strong>Admin Login:</strong> {result.adminPhone}</p>
                <p><strong>Plan:</strong> {result.plan}</p>
              </div>
              {/* Gentle BYOK + BYODB recommendation */}
              <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200 mt-3 text-left" data-testid="wizard-byok-byodb-tip">
                <p className="font-semibold text-emerald-800 text-sm">
                  {language === 'hi' ? '💡 अगला सुझाव — 100% FREE सेटअप' : '💡 Recommended next — 100% FREE setup'}
                </p>
                <ul className="text-xs text-emerald-700 mt-2 space-y-1 list-disc pl-4">
                  <li>{language === 'hi' ? 'Settings → Integrations में अपनी FREE Gemini API Key जोड़ें — सारे 31 AI features आपकी key पर चलेंगे।' : 'Add your FREE Gemini API Key in Settings → Integrations — all 31 AI features run on your key.'}</li>
                  <li>{language === 'hi' ? 'अपना FREE MongoDB (BYODB) जोड़ें — "आपका डेटा आपके पास", सालों तक ₹0 खर्च।' : 'Connect your FREE MongoDB (BYODB) — "your data stays with you", ₹0 for years.'}</li>
                </ul>
                <p className="text-[11px] text-emerald-600 mt-2">
                  {language === 'hi' ? 'दोनों optional हैं — बाद में कभी भी सेट कर सकते हैं। Guided setup साथ में मिलेगा।' : 'Both optional — set up anytime later. Guided setup included.'}
                </p>
              </div>
              <p className="text-gray-400 text-xs mt-3">{language === 'hi' ? 'School Admin अब login करके Setup Guide follow कर सकता है' : 'Admin can login and follow Setup Guide'}</p>
            </div>
          )}
        </div>

        <div className="p-5 border-t flex gap-3">
          {step > 1 && step <= 5 && (<button onClick={() => setStep(step - 1)} className="px-4 py-2 border rounded-xl text-gray-600"><i className="fa-solid fa-arrow-left mr-1"></i> {language === 'hi' ? 'पीछे' : 'Back'}</button>)}
          <div className="flex-1" />
          {step < 5 && (<button onClick={() => setStep(step + 1)} disabled={(step === 1 && (!school.name || !school.phone)) || (step === 2 && (!admin.phone || !admin.password))} className="px-6 py-2 bg-orange-500 text-white rounded-xl font-medium hover:bg-orange-600 disabled:opacity-40" data-testid="wiz-next-btn">{language === 'hi' ? 'आगे' : 'Next'} <i className="fa-solid fa-arrow-right ml-1"></i></button>)}
          {step === 5 && (<button onClick={handleCreate} disabled={loading} className="px-6 py-2 bg-green-500 text-white rounded-xl font-medium hover:bg-green-600 disabled:opacity-50" data-testid="wiz-create-btn">{loading ? '...' : (language === 'hi' ? 'School Onboard करें' : 'Onboard School')}</button>)}
          {step === 6 && (<button onClick={handleDone} className="px-6 py-2 bg-orange-500 text-white rounded-xl font-medium" data-testid="wiz-done-btn">Done</button>)}
        </div>
      </div>
    </div>
  );
}
